/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

// TODO: Many actions related to assets claim to pass an asset but
// only specify a subset of the required fields, which leads to a proliferation
// optionals. Might be worth splitting some of the types of `asset` fields
// out into dedicated types to keep optionals out of the state.
import TranscriptionState from "constants/TranscriptionState";
import { AudioBufferState } from "constants/AudioBufferState";
import { BlockToken } from "types/Block";
import { AdobeId } from "reducers/CurrentUserReducer";
import { TranscriptionService } from "constants/TranscriptionService";
import { AssetId } from "@phonos/types";
// TODO: we can also change this across the codebase to use the types from @phonos/types
// but it's a lot of files, might be best as separate folllow up PR
export type { AssetId };

export enum MediaType {
  AUDIO = "audio",
  VIDEO = "video",
}

export interface AssetUploadProgress {
  id: AssetId;
  progress: number;
}

export interface Asset {
  id: AssetId;
  mediaType: MediaType;
  participants?: AssetParticipantSet;
  transcript?: AssetTranscript;
  transcriptionState?: TranscriptionState;
  transcriptionService?: TranscriptionService;
  transcriptDetectedLanguage?: string;
  startedTranscriptionAt?: Date;
  studioSoundTrackId?: AssetId | null;
  originalTrackId?: AssetId | null;
  url?: string;
  streamingAudioV1Url?: string;
  streamingAudioV2Url?: string;
  streamingVideo360PUrl?: string;
  waveformUrl?: string;
  waveformComputationStarted?: boolean; // this is a frontend-only field
  audioBufferState?: AudioBufferState; // this is always present in the state but not in the `insertAsset` action.
  duration?: number;
  transcriptVersion?: string;
  enhancementErrorType?: string;
  enhancementErrorMessage?: string;
  hasAnyEnhancementErrors: boolean;
  file?: File;
}

export type AssetSet = Record<AssetId, Asset>;
export type AssetUploadSet = Record<AssetId, AssetUploadProgress>;
export type AssetParticipantSet = Record<number, AssetParticipant>;

type BehanceId = string;
export interface LegacyBehanceUserData {
  id: BehanceId;
}

export interface AssetParticipant {
  id: string;
  unidentifiedSpeakerNumber: number;
  adobeId?: AdobeId;
  displayName?: string;
  isAnonymous?: boolean;
}

export interface AssetTranscriptWord {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
}

export type SegmentId = string;

interface TranscriptSegmentBase {
  id: SegmentId;
  unidentifiedSpeakerNumber: number;
  adobeId?: AdobeId;
  displayName?: string;
  displayNameIsOverridden?: boolean;
  isAnonymous?: boolean;
  userData?: LegacyBehanceUserData;
}

export interface AssetTranscriptSegment extends TranscriptSegmentBase {
  words: AssetTranscriptWord[];
}

export interface BlockTranscriptSegment extends TranscriptSegmentBase {
  tokens: BlockToken[];
  segmentIndex: number;
}

export type AssetTranscript = AssetTranscriptSegment[];
